import React, { useState, useEffect, useMemo } from 'react';
import { Transaction } from '../types';
import { formatCurrency, formatDate, getVenezuelaTodayStr, formatDateTime } from '../utils/format';
import { 
  X, 
  User, 
  Phone, 
  MapPin, 
  DollarSign, 
  Calendar, 
  CalendarCheck, 
  CalendarDays, 
  Clock, 
  MessageSquare, 
  Check, 
  AlertCircle, 
  CheckCircle2, 
  Plus, 
  PlusCircle, 
  Search, 
  Trash2, 
  Loader2, 
  Edit3, 
  ChevronLeft, 
  ChevronRight,
  TrendingUp,
  FileText,
  CreditCard,
  Percent,
  ArrowRight
} from 'lucide-react';
import { useUI } from '../contexts/UIContext';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/axios';

export interface CalendarEvent {
  id: string;
  type: 'vencimiento';
  date: string; // YYYY-MM-DD
  clientName: string;
  phone?: string;
  transactionId?: string;
  amount?: number;
  status: 'pending' | 'completed';
  notes?: string;
  time?: string;
}

interface ClientDetailModalProps {
  client: {
    name: string;
    phone?: string;
    cedula?: string;
    location?: string;
    transactions: Transaction[];
    stats: {
      totalDebt: number;
      totalPaid: number;
      totalOutstanding: number;
      paymentRate: number;
    };
  };
  onClose: () => void;
  onUpdateClient?: (oldName: string, data: { client_name: string; client_document?: string; client_phone?: string }) => Promise<void> | void;
  onUpdateTransaction: (id: string, data: any) => Promise<void> | void;
  onDeleteTransaction: (id: string) => Promise<void> | void;
  onRegisterPayment: (id: string, amount: number, date?: string) => Promise<void> | void;
  onUpdatePayment: (txId: string, paymentId: number, amount: number) => Promise<void> | void;
  onDeletePayment: (txId: string, paymentId: number) => Promise<void> | void;
  onAddTransactionClick: () => void;
  onClientDetailsChange: (updated: any) => void;
  currentRate?: number | null;
}

type TabType = 'cuentas' | 'programados' | 'abonos' | 'descuentos';

export const ClientDetailModal: React.FC<ClientDetailModalProps> = ({
  client,
  onClose,
  onUpdateClient,
  onUpdateTransaction,
  onDeleteTransaction,
  onRegisterPayment,
  onUpdatePayment,
  onDeletePayment,
  onAddTransactionClick,
  onClientDetailsChange,
  currentRate,
}) => {
  const { toast, confirm } = useUI();
  const { businessId, isAdmin } = useAuth();

  const [activeTab, setActiveTab] = useState<TabType>('cuentas');
  const [refreshing, setRefreshing] = useState(false);

  // Client edit state
  const [editingClient, setEditingClient] = useState(false);
  const [savingClient, setSavingClient] = useState(false);

  // Search & Pagination in Accounts
  const [txSearch, setTxSearch] = useState('');
  const [txPage, setTxPage] = useState(1);
  const txItemsPerPage = 8;

  // Search & Pagination in Payments
  const [paymentPage, setPaymentPage] = useState(1);
  const paymentItemsPerPage = 8;

  // Editing Transaction Modal/Inline
  const [editingTx, setEditingTx] = useState<any | null>(null);
  const [savingTx, setSavingTx] = useState(false);

  // Sub-Modals: Programar Cobro & Reprogramar Cobro
  const [isProgramarOpen, setIsProgramarOpen] = useState(false);
  const [isReprogramarOpen, setIsReprogramarOpen] = useState(false);
  const [reprogramTarget, setReprogramTarget] = useState<{
    txId?: string;
    eventId?: string;
    currentDate: string;
    amount: number;
    notes?: string;
  } | null>(null);

  // Sub-Modal: Registrar Abono Individual
  const [paymentModalTx, setPaymentModalTx] = useState<any | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [savingPayment, setSavingPayment] = useState(false);

  // Sub-Modal: Registrar Abono Múltiple (Saldo Global + Cuenta a Cuenta)
  const [isMultiPaymentOpen, setIsMultiPaymentOpen] = useState(false);
  const [selectedMultiTxIds, setSelectedMultiTxIds] = useState<(string | number)[]>([]);
  const [multiTxAmounts, setMultiTxAmounts] = useState<Record<string | number, string>>({});
  const [globalMultiAmount, setGlobalMultiAmount] = useState<string>('');

  // Inline editing payment
  const [editingPayment, setEditingPayment] = useState<{ txId: string; paymentId: number; amount: number } | null>(null);

  // Programar Cobro Form State
  const [programForm, setProgramForm] = useState({
    associatedTxId: '',
    date: getVenezuelaTodayStr(),
    time: '09:00',
    amount: '',
    notes: ''
  });

  // Reprogramar Form State
  const [reprogramDate, setReprogramDate] = useState('');
  const [reprogramNotes, setReprogramNotes] = useState('');

  // Storage key for calendar sync
  const storageKey = `amigable_cobro_events_${businessId || 'default'}`;

  // Helper to get calendar events
  const getStoredEvents = (): CalendarEvent[] => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  };

  const saveStoredEvents = (events: CalendarEvent[]) => {
    localStorage.setItem(storageKey, JSON.stringify(events));
    window.dispatchEvent(new Event('storage'));
    window.dispatchEvent(new CustomEvent('calendar-events-updated', { detail: events }));
  };

  // State of custom programmed events for this client
  const [clientCalendarEvents, setClientCalendarEvents] = useState<CalendarEvent[]>([]);

  const loadClientEvents = () => {
    const all = getStoredEvents();
    const clientEvts = all.filter(e => 
      e.clientName && e.clientName.trim().toLowerCase() === client.name.trim().toLowerCase()
    );
    setClientCalendarEvents(clientEvts);
  };

  useEffect(() => {
    loadClientEvents();
  }, [client.name, businessId]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isProgramarOpen) setIsProgramarOpen(false);
        else if (isReprogramarOpen) setIsReprogramarOpen(false);
        else if (isMultiPaymentOpen) setIsMultiPaymentOpen(false);
        else if (paymentModalTx) setPaymentModalTx(null);
        else if (editingTx) setEditingTx(null);
        else if (editingClient) setEditingClient(false);
        else onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isProgramarOpen, isReprogramarOpen, isMultiPaymentOpen, paymentModalTx, editingTx, editingClient, onClose]);

  // Extract all payments
  const allPayments = useMemo(() => {
    const list: { txId: string; paymentId: number; amount: number; date: string; exchange_rate?: number; amount_bs?: number }[] = [];
    client.transactions.forEach((t: any) => {
      if (t.payments && t.payments.length > 0) {
        t.payments.forEach((p: any) => {
          list.push({
            txId: t.id,
            paymentId: p.id,
            amount: p.amount,
            date: p.date,
            exchange_rate: p.exchange_rate,
            amount_bs: p.amount_bs
          });
        });
      } else if (t.paidAmount > 0) {
        list.push({
          txId: t.id,
          paymentId: 0,
          amount: t.paidAmount,
          date: t.date
        });
      }
    });
    return list.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [client.transactions]);

  // Extract all discounts
  const allDiscounts = useMemo(() => {
    const list: { txId: string; percentage: number; amount: number; date: string }[] = [];
    client.transactions.forEach((t: any) => {
      if (t.discounts && t.discounts.length > 0) {
        t.discounts.forEach((d: any) => {
          list.push({
            txId: t.id,
            percentage: d.percentage,
            amount: d.amount,
            date: d.date
          });
        });
      }
    });
    return list.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [client.transactions]);

  // Combined Scheduled Collections (From transactions with dueDate + Custom programmed events)
  const scheduledCollections = useMemo(() => {
    const todayStr = getVenezuelaTodayStr();
    const list: {
      id: string;
      source: 'transaction' | 'custom';
      txId?: string;
      eventId?: string;
      date: string;
      time?: string;
      amount: number;
      status: 'pending' | 'completed';
      notes?: string;
      isOverdue: boolean;
      isToday: boolean;
    }[] = [];

    // 1. From pending/active transactions that have dueDate or date
    client.transactions.forEach((tx: any) => {
      if (tx.status === 'Cancelado') return;
      const isPaid = tx.status === 'Pagado';
      const outstanding = tx.amount - (Number(tx.paidAmount) || 0);
      const scheduledDate = tx.dueDate || tx.date;
      const isOver = !isPaid && scheduledDate < todayStr;
      const isTod = scheduledDate === todayStr;

      list.push({
        id: `tx-due-${tx.id}`,
        source: 'transaction',
        txId: String(tx.id),
        date: scheduledDate,
        time: '08:00',
        amount: isPaid ? tx.amount : (outstanding > 0 ? outstanding : tx.amount),
        status: isPaid ? 'completed' : 'pending',
        notes: `Vencimiento de Cuenta #${tx.id}`,
        isOverdue: isOver,
        isToday: isTod
      });
    });

    // 2. From custom calendar events that don't duplicate the transactionId
    clientCalendarEvents.forEach(evt => {
      if (evt.transactionId && list.some(i => i.txId === evt.transactionId)) {
        // Already represented from transaction, but update notes or time if available
        const item = list.find(i => i.txId === evt.transactionId);
        if (item) {
          if (evt.notes) item.notes = evt.notes;
          if (evt.time) item.time = evt.time;
          if (evt.date) item.date = evt.date;
        }
        return;
      }
      const isOver = evt.status !== 'completed' && evt.date < todayStr;
      const isTod = evt.date === todayStr;

      list.push({
        id: evt.id,
        source: 'custom',
        eventId: evt.id,
        txId: evt.transactionId,
        date: evt.date,
        time: evt.time || '09:00',
        amount: evt.amount || 0,
        status: evt.status,
        notes: evt.notes || 'Cobro programado',
        isOverdue: isOver,
        isToday: isTod
      });
    });

    // Sort by date ascending
    return list.sort((a, b) => a.date.localeCompare(b.date));
  }, [client.transactions, clientCalendarEvents]);

  // Filtered & Paginated Transactions
  const filteredTxs = useMemo(() => {
    return client.transactions.filter((t: any) => {
      if (!txSearch) return true;
      const s = txSearch.toLowerCase();
      return (
        t.id?.toString().includes(s) ||
        t.amount?.toString().includes(s) ||
        (t.description && t.description.toLowerCase().includes(s))
      );
    }).sort((a: any, b: any) => {
      const aPending = a.status !== 'Pagado' && a.status !== 'Cancelado' ? 1 : 0;
      const bPending = b.status !== 'Pagado' && b.status !== 'Cancelado' ? 1 : 0;
      if (aPending !== bPending) return bPending - aPending;
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }, [client.transactions, txSearch]);

  const totalTxPages = Math.max(1, Math.ceil(filteredTxs.length / txItemsPerPage));
  const paginatedTxs = useMemo(() => {
    return filteredTxs.slice((txPage - 1) * txItemsPerPage, txPage * txItemsPerPage);
  }, [filteredTxs, txPage]);

  // Paginated Payments
  const totalPaymentPages = Math.max(1, Math.ceil(allPayments.length / paymentItemsPerPage));
  const paginatedPayments = useMemo(() => {
    return allPayments.slice((paymentPage - 1) * paymentItemsPerPage, paymentPage * paymentItemsPerPage);
  }, [allPayments, paymentPage]);

  // Open Reprogram Modal for a specific target
  const handleOpenReprogram = (target: {
    txId?: string;
    eventId?: string;
    currentDate: string;
    amount: number;
    notes?: string;
  }) => {
    setReprogramTarget(target);
    setReprogramDate(target.currentDate || getVenezuelaTodayStr());
    setReprogramNotes(target.notes || '');
    setIsReprogramarOpen(true);
  };

  // Submit Reprogram
  const handleSubmitReprogram = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reprogramTarget || !reprogramDate) return;

    setRefreshing(true);
    try {
      // 1. If tied to a transaction, update backend due_date
      if (reprogramTarget.txId) {
        await onUpdateTransaction(reprogramTarget.txId, { due_date: reprogramDate });
        // Update local client transaction
        const updatedTxs = client.transactions.map((tx: any) => {
          if (String(tx.id) === String(reprogramTarget.txId)) {
            return { ...tx, dueDate: reprogramDate };
          }
          return tx;
        });
        onClientDetailsChange({ ...client, transactions: updatedTxs });
      }

      // 2. Update in calendar events localStorage
      const allEvts = getStoredEvents();
      let eventFound = false;
      const updatedEvts = allEvts.map(evt => {
        if (
          (reprogramTarget.eventId && evt.id === reprogramTarget.eventId) ||
          (reprogramTarget.txId && (evt.transactionId === reprogramTarget.txId || evt.id === `venc-${reprogramTarget.txId}`))
        ) {
          eventFound = true;
          return {
            ...evt,
            date: reprogramDate,
            notes: reprogramNotes || evt.notes,
          };
        }
        return evt;
      });

      if (!eventFound && reprogramTarget.txId) {
        // Create seeded event in calendar
        updatedEvts.push({
          id: `venc-${reprogramTarget.txId}`,
          type: 'vencimiento',
          date: reprogramDate,
          clientName: client.name,
          phone: client.phone,
          transactionId: reprogramTarget.txId,
          amount: reprogramTarget.amount,
          status: 'pending',
          notes: reprogramNotes || `Reprogramado para el ${reprogramDate}`,
          time: '09:00'
        });
      }

      saveStoredEvents(updatedEvts);
      loadClientEvents();

      toast(`Fecha reprogramada al ${formatDate(reprogramDate)}`, 'success');
      setIsReprogramarOpen(false);
      setReprogramTarget(null);
    } catch (err) {
      toast('Error al reprogramar cobro', 'error');
    } finally {
      setRefreshing(false);
    }
  };

  // Submit Programar Cobro
  const handleSubmitProgramar = async (e: React.FormEvent) => {
    e.preventDefault();
    const amountNum = parseFloat(programForm.amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      toast('Por favor ingresa un monto válido mayor a 0', 'error');
      return;
    }
    if (!programForm.date) {
      toast('Por favor selecciona una fecha', 'error');
      return;
    }

    setRefreshing(true);
    try {
      const selectedTx = client.transactions.find(t => String(t.id) === programForm.associatedTxId);

      // If associated to a transaction, update that transaction's due_date in backend
      if (selectedTx) {
        await onUpdateTransaction(selectedTx.id, { due_date: programForm.date });
        const updatedTxs = client.transactions.map((tx: any) => {
          if (String(tx.id) === String(selectedTx.id)) {
            return { ...tx, dueDate: programForm.date };
          }
          return tx;
        });
        onClientDetailsChange({ ...client, transactions: updatedTxs });
      }

      // Add to calendar events
      const allEvts = getStoredEvents();
      const newEvent: CalendarEvent = {
        id: `custom-cobro-${Date.now()}`,
        type: 'vencimiento',
        date: programForm.date,
        clientName: client.name,
        phone: client.phone,
        transactionId: selectedTx ? String(selectedTx.id) : undefined,
        amount: amountNum,
        status: 'pending',
        notes: programForm.notes || `Cobro programado por ${formatCurrency(amountNum)}`,
        time: programForm.time || '09:00'
      };

      saveStoredEvents([...allEvts, newEvent]);
      loadClientEvents();

      toast(`Cobro de ${formatCurrency(amountNum)} programado para el ${formatDate(programForm.date)}`, 'success');
      setIsProgramarOpen(false);
      setProgramForm({
        associatedTxId: '',
        date: getVenezuelaTodayStr(),
        time: '09:00',
        amount: '',
        notes: ''
      });
      setActiveTab('programados');
    } catch (err) {
      toast('Error al programar cobro', 'error');
    } finally {
      setRefreshing(false);
    }
  };

  // Delete custom scheduled event
  const handleDeleteCustomEvent = (eventId: string) => {
    confirm({
      title: 'Cancelar Cobro Programado',
      message: '¿Seguro que deseas eliminar este cobro agendado?',
      confirmText: 'Eliminar',
      type: 'danger',
      onConfirm: () => {
        const allEvts = getStoredEvents();
        const updated = allEvts.filter(e => e.id !== eventId);
        saveStoredEvents(updated);
        loadClientEvents();
        toast('Cobro agendado eliminado', 'info');
      }
    });
  };

  // WhatsApp reminder message
  const triggerWhatsApp = (amount?: number, dateStr?: string) => {
    if (!client.phone) {
      toast('El cliente no tiene teléfono registrado', 'warning');
      return;
    }
    const cleanPhone = client.phone.replace(/[^0-9]/g, '');
    let msg = `Hola ${client.name}, le saludamos cordialmente.`;
    if (amount && dateStr) {
      msg += ` Le recordamos su compromiso de pago por un monto de ${formatCurrency(amount)} con fecha pautada para el ${formatDate(dateStr)}.`;
    } else if (client.stats.totalOutstanding > 0) {
      msg += ` Le recordamos que presenta un saldo pendiente de ${formatCurrency(client.stats.totalOutstanding)}. Por favor contáctenos para su regularización.`;
    }
    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  // Quick preset dates for schedule form
  const setQuickDate = (daysAhead: number) => {
    const d = new Date();
    d.setDate(d.getDate() + daysAhead);
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    setProgramForm(prev => ({ ...prev, date: `${yr}-${mo}-${da}` }));
  };

  const pendingTxs = client.transactions.filter(t => t.status !== 'Pagado' && t.status !== 'Cancelado');

  // Multi Payment Handlers
  const handleOpenMultiPayment = (preselectedTxId?: string | number) => {
    const pending = client.transactions.filter((t: any) => t.status !== 'Pagado' && t.status !== 'Cancelado');
    if (pending.length === 0) return;
    const initialSelected = preselectedTxId 
      ? [preselectedTxId] 
      : pending.map((t: any) => t.id);
    setSelectedMultiTxIds(initialSelected);
    
    let total = 0;
    const initialAmounts: Record<string | number, string> = {};
    pending.forEach((t: any) => {
      if (initialSelected.includes(t.id)) {
        const debt = Math.max(0, t.amount - (Number(t.paidAmount) || 0));
        initialAmounts[t.id] = debt > 0 ? (Math.round(debt * 100) / 100).toFixed(2) : '';
        total += debt;
      } else {
        initialAmounts[t.id] = '';
      }
    });
    setMultiTxAmounts(initialAmounts);
    setGlobalMultiAmount(total > 0 ? (Math.round(total * 100) / 100).toFixed(2) : '');
    setIsMultiPaymentOpen(true);
  };

  const handleGlobalMultiAmountChange = (valStr: string) => {
    setGlobalMultiAmount(valStr);
    const totalVal = parseFloat(valStr) || 0;
    let remaining = totalVal;
    const nextAmounts: Record<string | number, string> = {};

    pendingTxs.forEach((tx: any) => {
      if (!selectedMultiTxIds.includes(tx.id)) {
        nextAmounts[tx.id] = '';
        return;
      }
      const debt = Math.max(0, tx.amount - (Number(tx.paidAmount) || 0));
      if (remaining <= 0) {
        nextAmounts[tx.id] = '0';
      } else if (remaining >= debt) {
        nextAmounts[tx.id] = debt.toFixed(2);
        remaining -= debt;
      } else {
        nextAmounts[tx.id] = (Math.round(remaining * 100) / 100).toFixed(2);
        remaining = 0;
      }
    });

    setMultiTxAmounts(nextAmounts);
  };

  const handleAccountMultiAmountChange = (txId: string | number, valStr: string) => {
    const nextAmounts = { ...multiTxAmounts, [txId]: valStr };
    setMultiTxAmounts(nextAmounts);

    const sum = selectedMultiTxIds.reduce((acc, id) => {
      const v = parseFloat(nextAmounts[id] || '0');
      return acc + (isNaN(v) ? 0 : v);
    }, 0);
    setGlobalMultiAmount(sum > 0 ? (Math.round(sum * 100) / 100).toFixed(2) : '');
  };

  const handleToggleMultiTx = (txId: string | number) => {
    const isSelected = selectedMultiTxIds.includes(txId);
    let nextSelected: (string | number)[];
    if (isSelected) {
      nextSelected = selectedMultiTxIds.filter(id => id !== txId);
      const nextAmounts = { ...multiTxAmounts, [txId]: '' };
      setMultiTxAmounts(nextAmounts);
      setSelectedMultiTxIds(nextSelected);

      const sum = nextSelected.reduce((acc, id) => {
        const v = parseFloat(nextAmounts[id] || '0');
        return acc + (isNaN(v) ? 0 : v);
      }, 0);
      setGlobalMultiAmount(sum > 0 ? (Math.round(sum * 100) / 100).toFixed(2) : '');
    } else {
      nextSelected = [...selectedMultiTxIds, txId];
      setSelectedMultiTxIds(nextSelected);
      const tx = pendingTxs.find((t: any) => t.id === txId);
      const debt = tx ? Math.max(0, tx.amount - (Number(tx.paidAmount) || 0)) : 0;
      const nextAmounts = { ...multiTxAmounts, [txId]: debt > 0 ? debt.toFixed(2) : '' };
      setMultiTxAmounts(nextAmounts);

      const sum = nextSelected.reduce((acc, id) => {
        const v = parseFloat(nextAmounts[id] || '0');
        return acc + (isNaN(v) ? 0 : v);
      }, 0);
      setGlobalMultiAmount(sum > 0 ? (Math.round(sum * 100) / 100).toFixed(2) : '');
    }
  };

  const handlePayAllSelectedDebts = () => {
    let total = 0;
    const nextAmounts: Record<string | number, string> = {};
    pendingTxs.forEach((tx: any) => {
      if (selectedMultiTxIds.includes(tx.id)) {
        const debt = Math.max(0, tx.amount - (Number(tx.paidAmount) || 0));
        nextAmounts[tx.id] = debt > 0 ? debt.toFixed(2) : '0';
        total += debt;
      } else {
        nextAmounts[tx.id] = '';
      }
    });
    setMultiTxAmounts(nextAmounts);
    setGlobalMultiAmount(total > 0 ? (Math.round(total * 100) / 100).toFixed(2) : '');
  };

  const handlePaySingleAccountMax = (tx: any) => {
    const debt = Math.max(0, tx.amount - (Number(tx.paidAmount) || 0));
    handleAccountMultiAmountChange(tx.id, debt.toFixed(2));
  };

  const handleConfirmMultiPayment = async () => {
    const toPayItems = selectedMultiTxIds
      .map(id => {
        const amt = parseFloat(multiTxAmounts[id] || '0');
        return { id: String(id), amount: amt };
      })
      .filter(item => item.amount > 0);

    if (toPayItems.length === 0) {
      toast('Ingresa un monto válido para al menos una cuenta', 'error');
      return;
    }

    setSavingPayment(true);
    try {
      for (const item of toPayItems) {
        await onRegisterPayment(item.id, item.amount);
      }
      toast(`Abono múltiple registrado con éxito (${toPayItems.length} cuenta${toPayItems.length > 1 ? 's' : ''})`, 'success');
      setIsMultiPaymentOpen(false);
    } catch (err) {
      toast('Error registrando los abonos', 'error');
    } finally {
      setSavingPayment(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[110] flex flex-col justify-end lg:justify-center items-center p-0 lg:p-4 bg-slate-950/70 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div 
        className="relative bg-white dark:bg-slate-900 rounded-t-[28px] lg:rounded-2xl shadow-2xl border-t lg:border border-slate-200 dark:border-slate-800 w-full max-w-7xl mx-auto overflow-hidden flex flex-col h-[94vh] lg:h-auto lg:max-h-[92vh] animate-slide-up lg:animate-scale-up"
        onClick={e => e.stopPropagation()}
      >
        {/* Mobile Drag Handle Pill */}
        <div 
          className="w-12 h-1.5 rounded-full bg-slate-300 dark:bg-slate-700 mx-auto mt-2.5 mb-1 shrink-0 lg:hidden cursor-pointer active:opacity-60" 
          onClick={onClose}
          title="Toca para cerrar"
        />

        {/* Loading Overlay */}
        {refreshing && (
          <div className="absolute inset-0 z-[130] bg-white/75 dark:bg-slate-900/75 backdrop-blur-[2px] flex flex-col items-center justify-center gap-2">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600 dark:text-indigo-400" />
            <span className="text-xs font-bold text-slate-700 dark:text-slate-200">Sincronizando ficha...</span>
          </div>
        )}

        {/* ══════════════════════════════════════════════════
            HEADER ADAPTATIVO (EXPERIENCIA APP NATIVA)
        ══════════════════════════════════════════════════ */}
        <div className="bg-slate-50/90 dark:bg-slate-900/80 p-3.5 sm:p-5 border-b border-slate-200 dark:border-slate-800 shrink-0">
          <div className="flex items-start justify-between gap-3">
            
            {/* Left: Avatar + Client Info / Edit Form */}
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-tr from-indigo-600 via-indigo-500 to-violet-500 text-white font-extrabold text-base sm:text-lg flex items-center justify-center shadow-md shadow-indigo-500/20 shrink-0">
                {client.name ? client.name.charAt(0).toUpperCase() : <User className="w-6 h-6" />}
              </div>

              {editingClient ? (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const fd = new FormData(e.target as HTMLFormElement);
                    const data = {
                      client_name: (fd.get('client_name') as string)?.trim() || client.name,
                      client_document: (fd.get('client_document') as string)?.trim() || '',
                      client_phone: (fd.get('client_phone') as string)?.trim() || '',
                    };
                    if (!data.client_name) return;
                    setSavingClient(true);
                    await onUpdateClient?.(client.name, data);
                    
                    const updatedTxs = client.transactions.map((tx: any) => ({
                      ...tx,
                      clientName: data.client_name,
                      cedula: data.client_document || tx.cedula,
                      phone: data.client_phone || tx.phone,
                    }));
                    
                    onClientDetailsChange({
                      ...client,
                      name: data.client_name,
                      phone: data.client_phone || client.phone,
                      cedula: data.client_document || client.cedula,
                      transactions: updatedTxs,
                    });
                    
                    setSavingClient(false);
                    setEditingClient(false);
                  }}
                  className="flex flex-col gap-2 flex-1 min-w-0"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      name="client_name"
                      defaultValue={client.name}
                      placeholder="Nombre del cliente"
                      className="border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-1.5 text-xs flex-1 min-w-[180px] bg-white dark:bg-slate-800 outline-none focus:ring-2 focus:ring-indigo-500 font-bold dark:text-slate-100"
                      required
                    />
                    <input
                      name="client_document"
                      defaultValue={client.cedula || ''}
                      placeholder="Cédula / Documento"
                      className="border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-1.5 text-xs w-36 bg-white dark:bg-slate-800 outline-none focus:ring-2 focus:ring-indigo-500 font-mono dark:text-slate-100"
                    />
                    <input
                      name="client_phone"
                      defaultValue={client.phone || ''}
                      placeholder="Teléfono (ej. +58414...)"
                      className="border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-1.5 text-xs w-36 bg-white dark:bg-slate-800 outline-none focus:ring-2 focus:ring-indigo-500 font-mono dark:text-slate-100"
                    />
                    <div className="flex items-center gap-1.5">
                      <button 
                        type="submit" 
                        disabled={savingClient} 
                        className="btn btn-primary px-3 py-1.5 text-xs flex items-center gap-1 cursor-pointer"
                      >
                        {savingClient ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        Guardar
                      </button>
                      <button 
                        type="button" 
                        disabled={savingClient} 
                        onClick={() => setEditingClient(false)} 
                        className="btn btn-secondary px-3 py-1.5 text-xs cursor-pointer"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                </form>
              ) : (
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-base sm:text-xl font-extrabold text-slate-900 dark:text-slate-100 truncate tracking-tight">
                      {client.name}
                    </h2>
                    {client.stats.totalOutstanding > 0 ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-rose-100 text-rose-800 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900/60 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3 text-rose-600 dark:text-rose-400" />
                        Debe {formatCurrency(client.stats.totalOutstanding)}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-emerald-100 text-emerald-800 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900/60 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                        Al Día
                      </span>
                    )}
                  </div>

                  {/* Badges / Contact buttons */}
                  <div className="flex items-center gap-2 sm:gap-3 text-xs text-slate-500 dark:text-slate-400 mt-1.5 flex-wrap">
                    {client.cedula && (
                      <span className="flex items-center gap-1 bg-white dark:bg-slate-800 px-2 py-0.5 rounded-lg border border-slate-200/60 dark:border-slate-800 font-mono text-[11px]">
                        <strong>C.I:</strong> {client.cedula}
                      </span>
                    )}

                    {client.phone && (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => triggerWhatsApp()}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:hover:bg-emerald-950/50 dark:text-emerald-300 text-[11px] font-bold border border-emerald-200 dark:border-emerald-900/50 transition-colors cursor-pointer"
                          title="Enviar mensaje por WhatsApp"
                        >
                          <MessageSquare className="w-3 h-3" /> WhatsApp
                        </button>
                        <a
                          href={`tel:${client.phone.replace(/[^0-9+]/g, '')}`}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 dark:bg-indigo-950/30 dark:hover:bg-indigo-950/50 dark:text-indigo-300 text-[11px] font-bold border border-indigo-200 dark:border-indigo-900/50 transition-colors"
                          title="Llamar directamente"
                        >
                          <Phone className="w-3 h-3" /> Llamar
                        </a>
                      </div>
                    )}

                    {client.location && (
                      <span className="flex items-center gap-1 text-[#06B6D4] text-[11px] font-medium truncate max-w-[200px]" title={client.location}>
                        <MapPin className="w-3 h-3 shrink-0" /> {client.location}
                      </span>
                    )}

                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => setEditingClient(true)}
                        className="p-1 text-slate-400 hover:text-indigo-600 hover:bg-slate-200/60 dark:hover:bg-slate-800 rounded-lg cursor-pointer transition-colors"
                        title="Editar datos del cliente"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right: Close Button */}
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 flex items-center justify-center text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100 active:scale-95 shadow-2xs cursor-pointer shrink-0 transition-all"
              title="Cerrar ficha (Esc)"
              aria-label="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════
            FINTECH METRICS / STATS (COMPACT & MODERN)
        ══════════════════════════════════════════════════ */}
        <div className="p-3.5 sm:p-5 bg-slate-100/50 dark:bg-slate-950/30 border-b border-slate-200 dark:border-slate-800 shrink-0">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
            <div className="bg-white dark:bg-slate-850 border border-slate-200/80 dark:border-slate-800 p-3 sm:p-3.5 rounded-2xl shadow-3xs">
              <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">Facturado Total</span>
              <span className="font-mono text-sm sm:text-lg font-extrabold text-slate-900 dark:text-slate-100 mt-0.5 block">
                {formatCurrency(client.stats.totalDebt)}
              </span>
            </div>

            <div className="bg-white dark:bg-slate-850 border border-slate-200/80 dark:border-slate-800 p-3 sm:p-3.5 rounded-2xl shadow-3xs">
              <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">Total Abonado</span>
              <span className="font-mono text-sm sm:text-lg font-extrabold text-emerald-600 dark:text-emerald-400 mt-0.5 block">
                {client.stats.totalPaid > 0 ? `+${formatCurrency(client.stats.totalPaid)}` : formatCurrency(client.stats.totalPaid)}
              </span>
            </div>

            <div className="bg-white dark:bg-slate-850 border border-slate-200/80 dark:border-slate-800 p-3 sm:p-3.5 rounded-2xl shadow-3xs">
              <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">Saldo Restante</span>
              <span className={`font-mono text-sm sm:text-lg font-extrabold mt-0.5 block ${
                client.stats.totalOutstanding > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'
              }`}>
                {formatCurrency(client.stats.totalOutstanding)}
              </span>
            </div>

            <div className="bg-white dark:bg-slate-850 border border-slate-200/80 dark:border-slate-800 p-3 sm:p-3.5 rounded-2xl shadow-3xs flex flex-col justify-between">
              <div className="flex justify-between items-center">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Efectividad</span>
                <span className="text-xs font-mono font-extrabold text-indigo-600 dark:text-indigo-400">
                  {client.stats.paymentRate.toFixed(0)}%
                </span>
              </div>
              <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2 overflow-hidden mt-1.5">
                <div 
                  className="bg-indigo-600 dark:bg-indigo-500 h-full rounded-full transition-all duration-500" 
                  style={{ width: `${client.stats.paymentRate}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════
            SEGMENTED NAVIGATION TABS (APP NATIVA STYLE)
        ══════════════════════════════════════════════════ */}
        <div className="px-3.5 sm:px-5 pt-3 pb-2 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between gap-2 overflow-x-auto no-scrollbar shrink-0">
          <div className="flex items-center gap-1.5 p-1 bg-slate-100 dark:bg-slate-800/80 rounded-2xl text-xs font-bold w-full sm:w-auto">
            <button
              type="button"
              onClick={() => setActiveTab('cuentas')}
              className={`flex-1 sm:flex-initial px-3.5 py-2 rounded-xl flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                activeTab === 'cuentas'
                  ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow-xs'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
              }`}
            >
              <FileText className="w-3.5 h-3.5" />
              <span>Cuentas</span>
              <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-slate-200/80 dark:bg-slate-800 font-mono">
                {client.transactions.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('programados')}
              className={`flex-1 sm:flex-initial px-3.5 py-2 rounded-xl flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                activeTab === 'programados'
                  ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow-xs'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
              }`}
            >
              <CalendarCheck className="w-3.5 h-3.5 text-indigo-500" />
              <span>Cobros Programados</span>
              <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300 font-mono font-extrabold">
                {scheduledCollections.filter(c => c.status === 'pending').length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('abonos')}
              className={`flex-1 sm:flex-initial px-3.5 py-2 rounded-xl flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                activeTab === 'abonos'
                  ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow-xs'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
              }`}
            >
              <CreditCard className="w-3.5 h-3.5" />
              <span>Abonos</span>
              <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-slate-200/80 dark:bg-slate-800 font-mono">
                {allPayments.length}
              </span>
            </button>

            {allDiscounts.length > 0 && (
              <button
                type="button"
                onClick={() => setActiveTab('descuentos')}
                className={`flex-1 sm:flex-initial px-3.5 py-2 rounded-xl flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                  activeTab === 'descuentos'
                    ? 'bg-white dark:bg-slate-700 text-purple-600 dark:text-purple-300 shadow-xs'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                }`}
              >
                <Percent className="w-3.5 h-3.5" />
                <span>Desc.</span>
                <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-purple-100 text-purple-800 dark:bg-purple-950 font-mono">
                  {allDiscounts.length}
                </span>
              </button>
            )}
          </div>

          {/* Quick Header Action (Desktop) */}
          <div className="hidden lg:flex items-center gap-2">
            {pendingTxs.length > 1 && (
              <button
                type="button"
                onClick={() => handleOpenMultiPayment()}
                className="btn btn-success px-3 py-1.5 text-xs flex items-center gap-1.5 cursor-pointer shadow-xs font-bold"
                title="Abonar a múltiples cuentas simultáneamente con saldo global o individual"
              >
                <DollarSign className="w-3.5 h-3.5" /> Abono Múltiple
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setProgramForm({
                  associatedTxId: pendingTxs[0] ? String(pendingTxs[0].id) : '',
                  date: getVenezuelaTodayStr(),
                  time: '09:00',
                  amount: pendingTxs[0] ? String(pendingTxs[0].amount - (Number(pendingTxs[0].paidAmount) || 0)) : '',
                  notes: ''
                });
                setIsProgramarOpen(true);
              }}
              className="btn btn-primary px-3 py-1.5 text-xs flex items-center gap-1.5 cursor-pointer shadow-xs font-bold"
            >
              <CalendarCheck className="w-3.5 h-3.5" /> Programar Cobro
            </button>
            <button
              type="button"
              onClick={onAddTransactionClick}
              className="btn btn-secondary px-3 py-1.5 text-xs flex items-center gap-1.5 cursor-pointer font-bold"
            >
              <PlusCircle className="w-3.5 h-3.5" /> Nueva Cuenta
            </button>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════
            TAB CONTENT AREA (SCROLLABLE & TOUCH FRIENDLY)
        ══════════════════════════════════════════════════ */}
        <div className="flex-1 min-h-0 overflow-y-auto p-3.5 sm:p-6 space-y-4 scrollbar-thin">

          {/* ──────────────────────────────────────────────────
              TAB 1: CUENTAS Y DEUDAS
          ────────────────────────────────────────────────── */}
          {activeTab === 'cuentas' && (
            <div className="space-y-3">
              {/* Filter & Add Row */}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[200px] max-w-md">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                  <input
                    type="text"
                    placeholder="Buscar por ID, monto o concepto..."
                    value={txSearch}
                    onChange={(e) => { setTxSearch(e.target.value); setTxPage(1); }}
                    className="w-full pl-9 pr-3 py-2 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-750 rounded-xl text-xs outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-100"
                  />
                  {txSearch && (
                    <button onClick={() => setTxSearch('')} className="absolute right-3 top-2 text-slate-400 text-xs">✕</button>
                  )}
                </div>

                <div className="flex items-center gap-2 lg:hidden">
                  {pendingTxs.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleOpenMultiPayment()}
                      className="btn btn-success text-xs flex items-center gap-1 py-2 px-2.5 font-bold shadow-xs"
                      title="Abono Múltiple"
                    >
                      <DollarSign className="w-4 h-4" />
                      <span>Abono Múltiple</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={onAddTransactionClick}
                    className="btn btn-secondary text-xs flex items-center gap-1.5 py-2 px-3"
                  >
                    <PlusCircle className="w-4 h-4 text-indigo-600" />
                    <span>Nueva Cuenta</span>
                  </button>
                </div>
              </div>

              {/* MOBILE CARDS VIEW (< lg) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:hidden">
                {paginatedTxs.length === 0 ? (
                  <div className="col-span-full py-12 text-center text-slate-400 text-xs font-semibold">
                    No se encontraron cuentas para este cliente.
                  </div>
                ) : (
                  paginatedTxs.map((t: any) => {
                    const tPaid = t.amount > 0 ? (t.status === 'Pagado' ? t.amount : (t.paidAmount || 0)) : (t.paidAmount || 0);
                    const remaining = Math.max(0, t.amount - tPaid);
                    const isPaid = t.status === 'Pagado';
                    const isCancelled = t.status === 'Cancelado';
                    const dueDateStr = t.dueDate || t.date;
                    const isOverdue = !isPaid && !isCancelled && dueDateStr < getVenezuelaTodayStr();

                    return (
                      <div 
                        key={t.id} 
                        className={`bg-white dark:bg-slate-850 p-4 rounded-2xl border shadow-3xs flex flex-col justify-between gap-3 transition-all ${
                          isOverdue 
                            ? 'border-rose-300 dark:border-rose-900/60 bg-rose-50/20' 
                            : isPaid 
                            ? 'border-emerald-200 dark:border-emerald-950/40' 
                            : 'border-slate-200 dark:border-slate-800'
                        }`}
                      >
                        {/* Top: ID + Date + Status */}
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-wider block">
                              Cuenta #{t.id} · Creada {formatDate(t.date)}
                            </span>
                            {t.description && (
                              <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mt-0.5 line-clamp-1">
                                {t.description}
                              </p>
                            )}
                          </div>
                          <span className={`px-2 py-0.5 rounded-md text-[9px] font-extrabold uppercase shrink-0 ${
                            isPaid 
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300' 
                              : isCancelled 
                              ? 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                              : isOverdue 
                              ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300'
                              : 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                          }`}>
                            {isPaid ? 'Pagado' : isCancelled ? 'Cancelado' : isOverdue ? 'Vencida' : 'Pendiente'}
                          </span>
                        </div>

                        {/* Middle: Amount & Remaining */}
                        <div className="grid grid-cols-2 gap-2 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-100 dark:border-slate-800">
                          <div>
                            <span className="text-[9px] font-bold text-slate-400 uppercase block">Total</span>
                            <span className="font-mono text-xs font-bold text-slate-850 dark:text-slate-200">
                              {formatCurrency(t.amount)}
                            </span>
                          </div>
                          <div>
                            <span className="text-[9px] font-bold text-slate-400 uppercase block">Saldo Pendiente</span>
                            <span className={`font-mono text-xs font-extrabold ${remaining > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600'}`}>
                              {formatCurrency(remaining)}
                            </span>
                          </div>
                        </div>

                        {/* Due Date Indicator */}
                        <div className="flex items-center justify-between text-[11px] pt-1">
                          <span className="text-slate-500 dark:text-slate-400 flex items-center gap-1 font-mono">
                            <Calendar className="w-3.5 h-3.5 text-indigo-500" />
                            <span>Vence: <strong>{t.dueDate ? formatDate(t.dueDate) : formatDate(t.date)}</strong></span>
                          </span>
                          {isOverdue && (
                            <span className="text-[10px] font-bold text-rose-600 dark:text-rose-400 flex items-center gap-0.5">
                              <AlertCircle className="w-3 h-3" /> Vencida
                            </span>
                          )}
                        </div>

                        {/* Action buttons (Touch target friendly >= 40px) */}
                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                          {!isPaid && !isCancelled && (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  setPaymentModalTx(t);
                                  setPaymentAmount(remaining > 0 ? String(remaining) : '');
                                }}
                                className="py-2.5 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white text-xs font-bold flex items-center justify-center gap-1.5 shadow-xs transition-all cursor-pointer"
                              >
                                <DollarSign className="w-3.5 h-3.5" /> Abonar
                              </button>

                              <button
                                type="button"
                                onClick={() => handleOpenReprogram({
                                  txId: String(t.id),
                                  currentDate: t.dueDate || t.date,
                                  amount: remaining,
                                  notes: `Reprogramación de Cuenta #${t.id}`
                                })}
                                className="py-2.5 px-3 rounded-xl bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/40 dark:hover:bg-indigo-950/70 text-indigo-700 dark:text-indigo-300 text-xs font-bold flex items-center justify-center gap-1.5 border border-indigo-200 dark:border-indigo-900/50 active:scale-95 transition-all cursor-pointer"
                              >
                                <CalendarCheck className="w-3.5 h-3.5" /> Reprogramar
                              </button>
                            </>
                          )}

                          <button
                            type="button"
                            onClick={() => setEditingTx(t)}
                            className="py-2 px-3 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-semibold flex items-center justify-center gap-1 cursor-pointer"
                          >
                            <Edit3 className="w-3 h-3" /> Editar
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              confirm({
                                title: 'Eliminar cuenta',
                                message: `¿Estás seguro de eliminar la cuenta #${t.id} por ${formatCurrency(t.amount)}? Esta acción es irreversible.`,
                                confirmText: 'Eliminar',
                                cancelText: 'Cancelar',
                                type: 'danger',
                                onConfirm: async () => {
                                  setRefreshing(true);
                                  await onDeleteTransaction(t.id);
                                  setRefreshing(false);
                                }
                              });
                            }}
                            className="py-2 px-3 rounded-xl bg-slate-100 hover:bg-rose-50 text-slate-500 hover:text-rose-600 dark:bg-slate-800 dark:hover:bg-rose-950/30 text-xs font-semibold flex items-center justify-center gap-1 cursor-pointer"
                          >
                            <Trash2 className="w-3 h-3" /> Eliminar
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* DESKTOP TABLE VIEW (>= lg) */}
              <div className="hidden lg:block bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-xs">
                <table className="w-full text-left text-xs whitespace-nowrap">
                  <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-450 font-bold uppercase tracking-wider border-b border-slate-200 dark:border-slate-800">
                    <tr>
                      <th className="px-4 py-3">#</th>
                      <th className="px-4 py-3">Creada</th>
                      <th className="px-4 py-3">Vence</th>
                      <th className="px-4 py-3">Monto</th>
                      <th className="px-4 py-3">Abonado</th>
                      <th className="px-4 py-3">Restante</th>
                      <th className="px-4 py-3">Estado</th>
                      <th className="px-4 py-3 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-medium">
                    {paginatedTxs.map((t: any, idx: number) => {
                      const tPaid = t.amount > 0 ? (t.status === 'Pagado' ? t.amount : (t.paidAmount || 0)) : (t.paidAmount || 0);
                      const remaining = Math.max(0, t.amount - tPaid);
                      const isPaid = t.status === 'Pagado';
                      const isCancelled = t.status === 'Cancelado';
                      const dueDateStr = t.dueDate || t.date;
                      const isOverdue = !isPaid && !isCancelled && dueDateStr < getVenezuelaTodayStr();

                      return (
                        <tr key={t.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-850/50 transition-colors">
                          <td className="px-4 py-3 font-mono text-slate-400">#{t.id}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-400 font-mono">{formatDate(t.date)}</td>
                          <td className="px-4 py-3 font-mono">
                            <div className="flex items-center gap-1.5">
                              <span className={isOverdue ? 'text-rose-600 dark:text-rose-400 font-bold' : 'text-slate-600 dark:text-slate-400'}>
                                {t.dueDate ? formatDate(t.dueDate) : formatDate(t.date)}
                              </span>
                              {isOverdue && (
                                <span className="text-[9px] px-1.5 py-0.2 rounded font-extrabold bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300">
                                  Vencida
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 font-bold font-mono text-slate-800 dark:text-slate-200">
                            {formatCurrency(t.amount)}
                          </td>
                          <td className="px-4 py-3 font-mono text-emerald-600 dark:text-emerald-400 font-bold">
                            {tPaid > 0 ? `+${formatCurrency(tPaid)}` : formatCurrency(tPaid)}
                          </td>
                          <td className="px-4 py-3 font-mono font-extrabold text-rose-600 dark:text-rose-400">
                            {formatCurrency(remaining)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded text-[9px] font-extrabold uppercase ${
                              isPaid ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300' :
                              isCancelled ? 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300' :
                              isOverdue ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300' :
                              'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                            }`}>
                              {isPaid ? 'Pagado' : isCancelled ? 'Cancelado' : isOverdue ? 'Vencida' : 'Pendiente'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              {!isPaid && !isCancelled && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handleOpenReprogram({
                                      txId: String(t.id),
                                      currentDate: t.dueDate || t.date,
                                      amount: remaining,
                                      notes: `Reprogramación de Cuenta #${t.id}`
                                    })}
                                    className="p-1.5 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 rounded-lg transition-colors cursor-pointer"
                                    title="Reprogramar fecha de vencimiento"
                                  >
                                    <CalendarCheck className="w-4 h-4" />
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => {
                                      setPaymentModalTx(t);
                                      setPaymentAmount(remaining > 0 ? String(remaining) : '');
                                    }}
                                    className="p-1.5 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 rounded-lg transition-colors cursor-pointer"
                                    title="Registrar Abono"
                                  >
                                    <DollarSign className="w-4 h-4" />
                                  </button>
                                </>
                              )}

                              <button
                                type="button"
                                onClick={() => setEditingTx(t)}
                                className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors cursor-pointer"
                                title="Editar cuenta"
                              >
                                <Edit3 className="w-4 h-4" />
                              </button>

                              <button
                                type="button"
                                onClick={() => {
                                  confirm({
                                    title: 'Eliminar cuenta',
                                    message: `¿Estás seguro de eliminar la cuenta #${t.id} por ${formatCurrency(t.amount)}?`,
                                    confirmText: 'Eliminar',
                                    cancelText: 'Cancelar',
                                    type: 'danger',
                                    onConfirm: async () => {
                                      setRefreshing(true);
                                      await onDeleteTransaction(t.id);
                                      setRefreshing(false);
                                    }
                                  });
                                }}
                                className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-lg transition-colors cursor-pointer"
                                title="Eliminar cuenta"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalTxPages > 1 && (
                <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-850 rounded-xl text-xs">
                  <span className="text-slate-500 font-semibold">Página {txPage} de {totalTxPages}</span>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      disabled={txPage === 1}
                      onClick={() => setTxPage(p => Math.max(1, p - 1))}
                      className="p-1.5 rounded-lg border bg-white dark:bg-slate-800 dark:border-slate-700 disabled:opacity-40 cursor-pointer"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      disabled={txPage === totalTxPages}
                      onClick={() => setTxPage(p => Math.min(totalTxPages, p + 1))}
                      className="p-1.5 rounded-lg border bg-white dark:bg-slate-800 dark:border-slate-700 disabled:opacity-40 cursor-pointer"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ──────────────────────────────────────────────────
              TAB 2: COBROS PROGRAMADOS (NUEVA EXPERIENCIA TOTAL)
          ────────────────────────────────────────────────── */}
          {activeTab === 'programados' && (
            <div className="space-y-4">
              {/* Sub-header */}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <h3 className="text-sm font-extrabold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                    <CalendarCheck className="w-4 h-4 text-indigo-600" />
                    Cobros y Vencimientos Programados
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Fechas límite pactadas y cobros sincronizados con el calendario de pagos.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setProgramForm({
                      associatedTxId: pendingTxs[0] ? String(pendingTxs[0].id) : '',
                      date: getVenezuelaTodayStr(),
                      time: '09:00',
                      amount: pendingTxs[0] ? String(pendingTxs[0].amount - (Number(pendingTxs[0].paidAmount) || 0)) : '',
                      notes: ''
                    });
                    setIsProgramarOpen(true);
                  }}
                  className="btn btn-primary text-xs font-bold flex items-center gap-1.5 py-2 px-3.5 shadow-xs cursor-pointer"
                >
                  <Plus className="w-4 h-4" /> Programar Nuevo Cobro
                </button>
              </div>

              {/* Collections Cards List */}
              {scheduledCollections.length === 0 ? (
                <div className="py-16 text-center text-slate-400 flex flex-col items-center justify-center bg-slate-50/50 dark:bg-slate-850/40 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800">
                  <CalendarDays className="w-12 h-12 mb-3 text-slate-300 dark:text-slate-700" />
                  <p className="font-bold text-sm text-slate-700 dark:text-slate-300">No hay cobros programados para {client.name}</p>
                  <p className="text-xs text-slate-400 mt-1 max-w-sm">
                    Puedes programar compromisos de pago con fecha y hora para que aparezcan automáticamente en el calendario.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setProgramForm({
                        associatedTxId: pendingTxs[0] ? String(pendingTxs[0].id) : '',
                        date: getVenezuelaTodayStr(),
                        time: '09:00',
                        amount: pendingTxs[0] ? String(pendingTxs[0].amount - (Number(pendingTxs[0].paidAmount) || 0)) : '',
                        notes: ''
                      });
                      setIsProgramarOpen(true);
                    }}
                    className="mt-4 btn btn-primary text-xs font-bold flex items-center gap-1.5 cursor-pointer"
                  >
                    <Plus className="w-4 h-4" /> Programar Primer Cobro
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {scheduledCollections.map((item) => {
                    const isCompleted = item.status === 'completed';

                    return (
                      <div 
                        key={item.id}
                        className={`p-4 rounded-2xl border shadow-3xs flex flex-col justify-between gap-3 transition-all ${
                          isCompleted
                            ? 'bg-emerald-50/20 border-emerald-200 dark:bg-emerald-950/10 dark:border-emerald-900/40 opacity-80'
                            : item.isOverdue
                            ? 'bg-rose-50/30 border-rose-300 dark:bg-rose-950/20 dark:border-rose-900/60'
                            : item.isToday
                            ? 'bg-indigo-50/30 border-indigo-300 dark:bg-indigo-950/20 dark:border-indigo-900/60 ring-1 ring-indigo-200 dark:ring-indigo-900/50'
                            : 'bg-white dark:bg-slate-850 border-slate-200 dark:border-slate-800'
                        }`}
                      >
                        {/* Header: Date + Status Badge */}
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400 shrink-0" />
                              <span className="font-extrabold text-xs text-slate-850 dark:text-slate-100 capitalize">
                                {formatDate(item.date)}
                              </span>
                              {item.time && (
                                <span className="text-[10px] text-slate-400 font-mono">
                                  · {item.time}
                                </span>
                              )}
                            </div>
                            <span className="text-[10px] text-slate-400 font-medium mt-0.5 block">
                              {item.txId ? `Cuenta #${item.txId}` : 'Cobro Libre'}
                            </span>
                          </div>

                          <span className={`px-2 py-0.5 rounded-md text-[9px] font-extrabold uppercase shrink-0 ${
                            isCompleted
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
                              : item.isOverdue
                              ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300'
                              : item.isToday
                              ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300'
                              : 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                          }`}>
                            {isCompleted ? 'Cobrada' : item.isOverdue ? 'Vencida' : item.isToday ? 'Hoy' : 'Programada'}
                          </span>
                        </div>

                        {/* Amount */}
                        <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-150 dark:border-slate-800 flex items-center justify-between">
                          <span className="text-[10px] font-bold text-slate-400 uppercase">Monto a Cobrar</span>
                          <span className={`font-mono text-sm font-extrabold ${
                            isCompleted ? 'text-emerald-600' : item.isOverdue ? 'text-rose-600' : 'text-slate-900 dark:text-slate-100'
                          }`}>
                            {formatCurrency(item.amount)}
                          </span>
                        </div>

                        {/* Notes */}
                        {item.notes && (
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 italic line-clamp-2 bg-slate-50/50 dark:bg-slate-900/30 p-2 rounded-lg">
                            "{item.notes}"
                          </p>
                        )}

                        {/* Action buttons */}
                        <div className="flex items-center gap-1.5 pt-2 border-t border-slate-150 dark:border-slate-800 text-xs">
                          {!isCompleted && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleOpenReprogram({
                                  txId: item.txId,
                                  eventId: item.eventId,
                                  currentDate: item.date,
                                  amount: item.amount,
                                  notes: item.notes
                                })}
                                className="flex-1 py-2 px-2.5 rounded-xl bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/40 dark:hover:bg-indigo-950/70 text-indigo-700 dark:text-indigo-300 font-bold flex items-center justify-center gap-1 border border-indigo-200 dark:border-indigo-900/50 active:scale-95 transition-all cursor-pointer"
                                title="Cambiar fecha de cobro"
                              >
                                <CalendarCheck className="w-3.5 h-3.5" /> Reprogramar
                              </button>

                              {item.txId && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const tx = client.transactions.find(t => String(t.id) === item.txId);
                                    if (tx) {
                                      setPaymentModalTx(tx);
                                      setPaymentAmount(String(item.amount));
                                    }
                                  }}
                                  className="py-2 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white font-bold flex items-center justify-center gap-1 shadow-xs transition-all cursor-pointer"
                                  title="Abonar cobro"
                                >
                                  <DollarSign className="w-3.5 h-3.5" /> Cobrar
                                </button>
                              )}
                            </>
                          )}

                          {client.phone && (
                            <button
                              type="button"
                              onClick={() => triggerWhatsApp(item.amount, item.date)}
                              className="p-2 rounded-xl bg-slate-100 hover:bg-emerald-50 text-slate-500 hover:text-emerald-600 dark:bg-slate-800 dark:hover:bg-emerald-950/40 transition-colors cursor-pointer"
                              title="Enviar recordatorio por WhatsApp"
                            >
                              <MessageSquare className="w-4 h-4" />
                            </button>
                          )}

                          {item.source === 'custom' && item.eventId && (
                            <button
                              type="button"
                              onClick={() => handleDeleteCustomEvent(item.eventId!)}
                              className="p-2 rounded-xl bg-slate-100 hover:bg-rose-50 text-slate-400 hover:text-rose-600 dark:bg-slate-800 transition-colors cursor-pointer"
                              title="Eliminar cobro agendado"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ──────────────────────────────────────────────────
              TAB 3: ABONOS REALIZADOS
          ────────────────────────────────────────────────── */}
          {activeTab === 'abonos' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-bold text-slate-850 dark:text-slate-200">
                  Historial de Abonos ({allPayments.length})
                </span>
                {pendingTxs.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      if (pendingTxs.length === 1) {
                        const firstPending = pendingTxs[0];
                        setPaymentModalTx(firstPending);
                        const rem = firstPending.amount - (Number(firstPending.paidAmount) || 0);
                        setPaymentAmount(rem > 0 ? String(rem) : '');
                      } else {
                        handleOpenMultiPayment();
                      }
                    }}
                    className="btn btn-success text-xs font-bold flex items-center gap-1.5 py-1.5 px-3 cursor-pointer shadow-xs"
                  >
                    <DollarSign className="w-3.5 h-3.5" />
                    {pendingTxs.length > 1 ? `Abono Múltiple (${pendingTxs.length})` : 'Registrar Abono'}
                  </button>
                )}
              </div>

              {allPayments.length === 0 ? (
                <div className="py-12 text-center text-slate-400 text-xs font-semibold">
                  No se han registrado abonos para este cliente todavía.
                </div>
              ) : (
                <div className="space-y-2">
                  {paginatedPayments.map((p, idx) => {
                    const isEditing = editingPayment?.txId === p.txId && editingPayment?.paymentId === p.paymentId;

                    return (
                      <div 
                        key={`${p.txId}-${p.paymentId}-${idx}`}
                        className="bg-white dark:bg-slate-850 border border-slate-200 dark:border-slate-800 p-3.5 rounded-2xl flex items-center justify-between gap-3 shadow-3xs"
                      >
                        {isEditing ? (
                          <form
                            onSubmit={async (e) => {
                              e.preventDefault();
                              const input = (e.target as HTMLFormElement).querySelector('input')!;
                              const val = parseFloat(input.value);
                              if (!val || val <= 0) return;
                              setSavingPayment(true);
                              await onUpdatePayment(p.txId, p.paymentId, val);
                              setSavingPayment(false);
                              setEditingPayment(null);
                            }}
                            className="flex items-center gap-2 flex-1"
                          >
                            <input
                              type="number"
                              step="0.01"
                              defaultValue={p.amount}
                              className="w-28 border border-slate-300 dark:border-slate-700 rounded-xl px-2.5 py-1.5 text-xs font-mono bg-white dark:bg-slate-800 outline-none focus:ring-2 focus:ring-indigo-500 font-bold"
                              autoFocus
                            />
                            <button type="submit" disabled={savingPayment} className="btn btn-primary px-3 py-1.5 text-xs">
                              {savingPayment ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Guardar'}
                            </button>
                            <button type="button" onClick={() => setEditingPayment(null)} className="btn btn-secondary px-2.5 py-1.5 text-xs">
                              Cancelar
                            </button>
                          </form>
                        ) : (
                          <>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-extrabold text-emerald-600 dark:text-emerald-400 font-mono text-sm sm:text-base">
                                  +{formatCurrency(p.amount)}
                                </span>
                                <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500">
                                  Cuenta #{p.txId}
                                </span>
                              </div>
                              <span className="text-[11px] text-slate-400 font-mono mt-0.5 block">
                                {formatDateTime(p.date)}
                              </span>
                              {p.amount_bs && p.exchange_rate && (
                                <span className="text-[10px] text-slate-400 font-mono">
                                  Eq. {p.amount_bs.toLocaleString('es-VE', { minimumFractionDigits: 2 })} Bs.
                                </span>
                              )}
                            </div>

                            <div className="flex items-center gap-1.5">
                              {isAdmin && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => setEditingPayment({ txId: p.txId, paymentId: p.paymentId, amount: p.amount })}
                                    className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors cursor-pointer"
                                    title="Editar monto del abono"
                                  >
                                    <Edit3 className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      confirm({
                                        title: 'Eliminar abono',
                                        message: `¿Eliminar abono de ${formatCurrency(p.amount)} de la cuenta #${p.txId}?`,
                                        confirmText: 'Eliminar',
                                        cancelText: 'Cancelar',
                                        type: 'danger',
                                        onConfirm: async () => {
                                          setRefreshing(true);
                                          await onDeletePayment(p.txId, p.paymentId);
                                          setRefreshing(false);
                                        }
                                      });
                                    }}
                                    className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-xl transition-colors cursor-pointer"
                                    title="Eliminar abono"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {totalPaymentPages > 1 && (
                <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-850 rounded-xl text-xs">
                  <span className="text-slate-500 font-semibold">Página {paymentPage} de {totalPaymentPages}</span>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      disabled={paymentPage === 1}
                      onClick={() => setPaymentPage(p => Math.max(1, p - 1))}
                      className="p-1.5 rounded-lg border bg-white dark:bg-slate-800 dark:border-slate-700 disabled:opacity-40 cursor-pointer"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      disabled={paymentPage === totalPaymentPages}
                      onClick={() => setPaymentPage(p => Math.min(totalPaymentPages, p + 1))}
                      className="p-1.5 rounded-lg border bg-white dark:bg-slate-800 dark:border-slate-700 disabled:opacity-40 cursor-pointer"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ──────────────────────────────────────────────────
              TAB 4: DESCUENTOS APLICADOS
          ────────────────────────────────────────────────── */}
          {activeTab === 'descuentos' && (
            <div className="space-y-3">
              <span className="text-xs font-bold text-slate-850 dark:text-slate-200">
                Historial de Descuentos ({allDiscounts.length})
              </span>
              {allDiscounts.length === 0 ? (
                <div className="py-12 text-center text-slate-400 text-xs font-semibold">
                  No se han registrado descuentos para este cliente.
                </div>
              ) : (
                <div className="space-y-2">
                  {allDiscounts.map((d, idx) => (
                    <div 
                      key={idx}
                      className="bg-white dark:bg-slate-850 border border-slate-200 dark:border-slate-800 p-3.5 rounded-2xl flex items-center justify-between gap-3 shadow-3xs"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-extrabold text-purple-600 dark:text-purple-400 font-mono text-sm sm:text-base">
                            -{formatCurrency(d.amount)}
                          </span>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300">
                            Descuento {d.percentage}%
                          </span>
                        </div>
                        <span className="text-[11px] text-slate-400 font-mono mt-0.5 block">
                          Aplicado el {formatDateTime(d.date)} en Cuenta #{d.txId}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>

        {/* ══════════════════════════════════════════════════
            STICKY BOTTOM ACTION BAR (MOBILE & TABLET NATIVE)
        ══════════════════════════════════════════════════ */}
        <div className="p-3 sm:p-4 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-slate-200 dark:border-slate-800 flex items-center justify-between gap-2 shrink-0">
          <div className="flex items-center gap-2 flex-1">
            <button
              type="button"
              onClick={() => {
                setProgramForm({
                  associatedTxId: pendingTxs[0] ? String(pendingTxs[0].id) : '',
                  date: getVenezuelaTodayStr(),
                  time: '09:00',
                  amount: pendingTxs[0] ? String(pendingTxs[0].amount - (Number(pendingTxs[0].paidAmount) || 0)) : '',
                  notes: ''
                });
                setIsProgramarOpen(true);
              }}
              className="flex-1 btn btn-primary py-3 text-xs sm:text-sm font-bold flex items-center justify-center gap-2 shadow-md cursor-pointer active:scale-[0.98] transition-all"
            >
              <CalendarCheck className="w-4 h-4" />
              <span>Programar Cobro</span>
            </button>

            <button
              type="button"
              onClick={onAddTransactionClick}
              className="btn btn-secondary py-3 px-3.5 text-xs sm:text-sm font-bold flex items-center justify-center gap-1.5 cursor-pointer active:scale-[0.98] transition-all"
              title="Crear nueva cuenta"
            >
              <PlusCircle className="w-4 h-4 text-indigo-600" />
              <span className="hidden sm:inline">Nueva Cuenta</span>
            </button>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="py-3 px-4 rounded-xl text-xs font-bold text-slate-500 hover:text-slate-800 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
          >
            Cerrar
          </button>
        </div>

      </div>

      {/* ══════════════════════════════════════════════════
          SUB-MODAL: PROGRAMAR COBRO
      ══════════════════════════════════════════════════ */}
      {isProgramarOpen && (
        <div 
          className="fixed inset-0 z-[140] flex items-center justify-center bg-slate-950/60 backdrop-blur-xs p-4 animate-fade-in"
          onClick={() => setIsProgramarOpen(false)}
        >
          <div 
            className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-md overflow-hidden animate-scale-up"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 sm:p-5 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-850">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-indigo-50 dark:bg-indigo-950 text-indigo-600 flex items-center justify-center">
                  <CalendarCheck className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-extrabold text-slate-900 dark:text-slate-100">Programar Cobro</h3>
                  <span className="text-[10px] text-slate-400 font-semibold">{client.name}</span>
                </div>
              </div>
              <button 
                type="button"
                onClick={() => setIsProgramarOpen(false)} 
                className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmitProgramar} className="p-5 space-y-4 text-xs">
              {/* Associated Account (if pending exists) */}
              <div>
                <label className="block font-bold mb-1 text-slate-700 dark:text-slate-300">
                  Asociar a Cuenta / Deuda
                </label>
                <select
                  value={programForm.associatedTxId}
                  onChange={(e) => {
                    const txId = e.target.value;
                    const tx = client.transactions.find(t => String(t.id) === txId);
                    const rem = tx ? tx.amount - (Number(tx.paidAmount) || 0) : 0;
                    setProgramForm(prev => ({
                      ...prev,
                      associatedTxId: txId,
                      amount: rem > 0 ? String(rem) : prev.amount
                    }));
                  }}
                  className="w-full border dark:border-slate-750 p-2.5 rounded-xl bg-white dark:bg-slate-850 dark:text-slate-200 outline-none focus:ring-2 focus:ring-indigo-500 font-semibold"
                >
                  <option value="">-- Cobro Libre (Sin asociar a cuenta específica) --</option>
                  {pendingTxs.map(t => {
                    const rem = t.amount - (Number(t.paidAmount) || 0);
                    return (
                      <option key={t.id} value={t.id}>
                        Cuenta #{t.id} - Saldo pendiente: {formatCurrency(rem)}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className="block font-bold mb-1 text-slate-700 dark:text-slate-300">
                    Fecha del Cobro *
                  </label>
                  <input
                    type="date"
                    required
                    value={programForm.date}
                    onChange={(e) => setProgramForm(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full border dark:border-slate-750 p-2.5 rounded-xl bg-white dark:bg-slate-850 dark:text-slate-200 outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
                  />
                </div>
                <div>
                  <label className="block font-bold mb-1 text-slate-700 dark:text-slate-300">
                    Hora
                  </label>
                  <input
                    type="time"
                    value={programForm.time}
                    onChange={(e) => setProgramForm(prev => ({ ...prev, time: e.target.value }))}
                    className="w-full border dark:border-slate-750 p-2.5 rounded-xl bg-white dark:bg-slate-850 dark:text-slate-200 outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
                  />
                </div>
              </div>

              {/* Quick Date Presets */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] font-bold text-slate-400">Atajos:</span>
                <button
                  type="button"
                  onClick={() => setQuickDate(0)}
                  className="px-2 py-0.5 rounded-md bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 text-[10px] font-bold cursor-pointer"
                >
                  Hoy
                </button>
                <button
                  type="button"
                  onClick={() => setQuickDate(1)}
                  className="px-2 py-0.5 rounded-md bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 text-[10px] font-bold cursor-pointer"
                >
                  Mañana
                </button>
                <button
                  type="button"
                  onClick={() => setQuickDate(7)}
                  className="px-2 py-0.5 rounded-md bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 text-[10px] font-bold cursor-pointer"
                >
                  En 7 días
                </button>
                <button
                  type="button"
                  onClick={() => setQuickDate(15)}
                  className="px-2 py-0.5 rounded-md bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 text-[10px] font-bold cursor-pointer"
                >
                  En 15 días
                </button>
              </div>

              {/* Amount */}
              <div>
                <label className="block font-bold mb-1 text-slate-700 dark:text-slate-300">
                  Monto a Cobrar ($) *
                </label>
                <div className="relative">
                  <DollarSign className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                  <input
                    type="number"
                    step="any"
                    min="0.01"
                    required
                    placeholder="0.00"
                    value={programForm.amount}
                    onChange={(e) => setProgramForm(prev => ({ ...prev, amount: e.target.value }))}
                    className="w-full border dark:border-slate-750 pl-9 pr-3 py-2.5 rounded-xl bg-white dark:bg-slate-850 dark:text-slate-200 outline-none focus:ring-2 focus:ring-indigo-500 font-mono font-bold text-sm"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block font-bold mb-1 text-slate-700 dark:text-slate-300">
                  Observaciones / Compromiso
                </label>
                <textarea
                  rows={2}
                  placeholder="Detalle del cobro o acuerdo con el cliente..."
                  value={programForm.notes}
                  onChange={(e) => setProgramForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full border dark:border-slate-750 p-2.5 rounded-xl bg-white dark:bg-slate-850 dark:text-slate-200 outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsProgramarOpen(false)}
                  className="btn btn-secondary py-2 px-4 cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary py-2 px-4 font-bold flex items-center gap-1.5 cursor-pointer shadow-xs"
                >
                  <CalendarCheck className="w-3.5 h-3.5" /> Programar Cobro
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          SUB-MODAL: REPROGRAMAR COBRO
      ══════════════════════════════════════════════════ */}
      {isReprogramarOpen && reprogramTarget && (
        <div 
          className="fixed inset-0 z-[140] flex items-center justify-center bg-slate-950/60 backdrop-blur-xs p-4 animate-fade-in"
          onClick={() => setIsReprogramarOpen(false)}
        >
          <div 
            className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-sm overflow-hidden animate-scale-up"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 sm:p-5 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-850">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-indigo-50 dark:bg-indigo-950 text-indigo-600 flex items-center justify-center">
                  <CalendarCheck className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-extrabold text-slate-900 dark:text-slate-100">Reprogramar Cobro</h3>
                  <span className="text-[10px] text-slate-400 font-semibold">{client.name}</span>
                </div>
              </div>
              <button 
                type="button"
                onClick={() => setIsReprogramarOpen(false)} 
                className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmitReprogram} className="p-5 space-y-4 text-xs">
              {/* Target info card */}
              <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-850 border border-slate-200/80 dark:border-slate-800 flex justify-between items-center">
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-400 block">Fecha Actual</span>
                  <span className="font-mono font-bold text-slate-700 dark:text-slate-300">
                    {formatDate(reprogramTarget.currentDate)}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-[10px] uppercase font-bold text-slate-400 block">Monto</span>
                  <span className="font-mono font-extrabold text-indigo-600 dark:text-indigo-400">
                    {formatCurrency(reprogramTarget.amount)}
                  </span>
                </div>
              </div>

              {/* New Date */}
              <div>
                <label className="block font-bold mb-1 text-slate-700 dark:text-slate-300">
                  Nueva Fecha de Cobro *
                </label>
                <input
                  type="date"
                  required
                  value={reprogramDate}
                  onChange={(e) => setReprogramDate(e.target.value)}
                  className="w-full border dark:border-slate-750 p-2.5 rounded-xl bg-white dark:bg-slate-850 dark:text-slate-200 outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block font-bold mb-1 text-slate-700 dark:text-slate-300">
                  Motivo / Observaciones
                </label>
                <textarea
                  rows={2}
                  placeholder="Por qué se reprograma la fecha..."
                  value={reprogramNotes}
                  onChange={(e) => setReprogramNotes(e.target.value)}
                  className="w-full border dark:border-slate-750 p-2.5 rounded-xl bg-white dark:bg-slate-850 dark:text-slate-200 outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsReprogramarOpen(false)}
                  className="btn btn-secondary py-2 px-4 cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary py-2 px-4 font-bold flex items-center gap-1.5 cursor-pointer shadow-xs"
                >
                  <Check className="w-3.5 h-3.5" /> Confirmar Fecha
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          SUB-MODAL: REGISTRAR ABONO
      ══════════════════════════════════════════════════ */}
      {paymentModalTx && (() => {
        const tx = paymentModalTx;
        const paid = tx.paidAmount || 0;
        const currentDebt = tx.amount - paid;
        const paymentVal = parseFloat(paymentAmount) || 0;
        const newBalance = Math.max(0, currentDebt - paymentVal);

        return (
          <div 
            className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-950/65 backdrop-blur-xs p-4 animate-fade-in"
            onClick={() => setPaymentModalTx(null)}
          >
            <div 
              className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-sm overflow-hidden animate-scale-up"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-4 sm:p-5 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-850">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-xl bg-emerald-50 dark:bg-emerald-950 text-emerald-600 flex items-center justify-center">
                    <DollarSign className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-extrabold text-slate-900 dark:text-slate-100">
                      Registrar Abono · Cuenta #{tx.id}
                    </h3>
                    <span className="text-[10px] text-slate-400 font-semibold">{client.name}</span>
                  </div>
                </div>
                <button 
                  type="button"
                  onClick={() => setPaymentModalTx(null)} 
                  className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-5 space-y-4 text-xs">
                {/* Balance preview */}
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-850 border border-slate-200 dark:border-slate-800 space-y-1.5">
                  <div className="flex justify-between items-center text-slate-500">
                    <span>Deuda actual:</span>
                    <span className="font-mono font-bold text-rose-600 dark:text-rose-400">{formatCurrency(currentDebt)}</span>
                  </div>
                  <div className="flex justify-between items-center text-slate-500 pt-1 border-t border-slate-200/60 dark:border-slate-800">
                    <span>Nuevo saldo restante:</span>
                    <span className={`font-mono font-extrabold ${newBalance === 0 ? 'text-emerald-600' : 'text-slate-800 dark:text-slate-200'}`}>
                      {formatCurrency(newBalance)}
                    </span>
                  </div>
                </div>

                {/* Amount input */}
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="font-bold text-slate-700 dark:text-slate-300">Monto del Abono ($)</label>
                    <button
                      type="button"
                      onClick={() => setPaymentAmount(String(currentDebt))}
                      className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
                    >
                      Pagar Total ({formatCurrency(currentDebt)})
                    </button>
                  </div>
                  <div className="relative">
                    <DollarSign className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                    <input
                      type="number"
                      step="any"
                      min="0.01"
                      required
                      autoFocus
                      placeholder="0.00"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      className="w-full border dark:border-slate-750 pl-9 pr-3 py-2.5 rounded-xl bg-white dark:bg-slate-850 dark:text-slate-200 outline-none focus:ring-2 focus:ring-indigo-500 font-mono font-bold text-sm"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setPaymentModalTx(null)}
                    className="btn btn-secondary py-2 px-4 cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    disabled={!paymentVal || paymentVal <= 0 || savingPayment}
                    onClick={async () => {
                      setSavingPayment(true);
                      await onRegisterPayment(tx.id, paymentVal);
                      setSavingPayment(false);
                      setPaymentModalTx(null);
                      setPaymentAmount('');
                    }}
                    className="btn btn-success py-2 px-4 font-bold flex items-center gap-1.5 cursor-pointer shadow-xs disabled:opacity-50"
                  >
                    {savingPayment ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    Confirmar Abono
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ══════════════════════════════════════════════════
          SUB-MODAL: REGISTRAR ABONO MÚLTIPLE (SALDO GLOBAL + CUENTA A CUENTA)
      ══════════════════════════════════════════════════ */}
      {isMultiPaymentOpen && (() => {
        const totalSelectedDebt = pendingTxs
          .filter((tx: any) => selectedMultiTxIds.includes(tx.id))
          .reduce((acc: number, tx: any) => acc + Math.max(0, tx.amount - (Number(tx.paidAmount) || 0)), 0);

        const totalToPayNow = selectedMultiTxIds.reduce((acc: number, id) => {
          const amt = parseFloat(multiTxAmounts[id] || '0');
          return acc + (isNaN(amt) ? 0 : amt);
        }, 0);

        const newRemainingDebt = Math.max(0, totalSelectedDebt - totalToPayNow);

        return (
          <div
            className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-950/70 backdrop-blur-xs p-3 sm:p-4 animate-fade-in"
            onClick={() => setIsMultiPaymentOpen(false)}
          >
            <div
              className="bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-lg overflow-hidden animate-scale-up flex flex-col max-h-[92vh]"
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="p-4 sm:p-5 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-850 shrink-0">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                    <DollarSign className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-extrabold text-slate-900 dark:text-slate-100">
                      Registrar Abono Múltiple
                    </h3>
                    <span className="text-[11px] text-slate-400 font-semibold">{client.name}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsMultiPaymentOpen(false)}
                  className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Modal Body (Scrollable) */}
              <div className="p-4 sm:p-5 space-y-4 overflow-y-auto scrollbar-thin text-xs">

                {/* Section 1: Saldo Global / Total a Distribuir */}
                <div className="p-3.5 sm:p-4 rounded-2xl bg-indigo-50/70 dark:bg-indigo-950/20 border border-indigo-200/70 dark:border-indigo-900/40 space-y-2.5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5">
                    <div>
                      <label className="font-extrabold text-slate-800 dark:text-slate-200 flex items-center gap-1.5 text-xs">
                        <DollarSign className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                        Monto Total a Abonar (Saldo Global)
                      </label>
                      <p className="text-[10.5px] text-slate-500 dark:text-slate-400">
                        Ingresa un saldo total para distribuirlo en cascada, o edita cuenta a cuenta abajo.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handlePayAllSelectedDebts}
                      className="text-[10px] sm:text-[11px] font-extrabold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer self-start sm:self-auto bg-indigo-100/60 dark:bg-indigo-900/40 px-2 py-1 rounded-lg"
                    >
                      Pagar Total ({formatCurrency(totalSelectedDebt)})
                    </button>
                  </div>

                  <div className="relative">
                    <DollarSign className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                    <input
                      type="number"
                      step="any"
                      min="0.01"
                      placeholder="0.00"
                      value={globalMultiAmount}
                      onChange={e => handleGlobalMultiAmountChange(e.target.value)}
                      className="w-full border border-indigo-200 dark:border-indigo-900/60 pl-9 pr-3 py-2 rounded-xl bg-white dark:bg-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-indigo-500 font-mono font-bold text-base"
                      autoFocus
                    />
                    {currentRate && totalToPayNow > 0 && (
                      <div className="text-[10.5px] text-slate-500 dark:text-slate-400 mt-1 font-medium font-mono">
                        Eq. {(totalToPayNow * currentRate).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Bs.
                      </div>
                    )}
                  </div>
                </div>

                {/* Section 2: Lista de cuentas a abonar */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-slate-700 dark:text-slate-300">
                      Selecciona las cuentas a abonar ({selectedMultiTxIds.length} de {pendingTxs.length}):
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        const allIds = pendingTxs.map((t: any) => t.id);
                        setSelectedMultiTxIds(allIds);
                        handlePayAllSelectedDebts();
                      }}
                      className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      Seleccionar todas
                    </button>
                  </div>

                  <div className="max-h-[240px] overflow-y-auto space-y-2 rounded-xl p-2 border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-950/40 scrollbar-thin">
                    {pendingTxs.map((tx: any) => {
                      const currentDebt = Math.max(0, tx.amount - (Number(tx.paidAmount) || 0));
                      const isSelected = selectedMultiTxIds.includes(tx.id);
                      const assignedVal = multiTxAmounts[tx.id] || '';
                      const assignedNum = parseFloat(assignedVal) || 0;
                      const accountNewRest = Math.max(0, currentDebt - assignedNum);

                      return (
                        <div
                          key={tx.id}
                          className={`p-2.5 rounded-xl border transition-all ${
                            isSelected
                              ? 'bg-white dark:bg-slate-850 border-emerald-300 dark:border-emerald-800/80 shadow-xs'
                              : 'bg-white/60 dark:bg-slate-850/40 border-slate-200 dark:border-slate-800 opacity-60'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <label className="flex items-center gap-2.5 cursor-pointer flex-1">
                              <input
                                type="checkbox"
                                className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500 cursor-pointer"
                                checked={isSelected}
                                onChange={() => handleToggleMultiTx(tx.id)}
                              />
                              <div className="flex flex-col">
                                <span className="font-bold text-slate-800 dark:text-slate-200 text-xs">
                                  Cuenta #{tx.id} · {formatDate(tx.dueDate || tx.date)}
                                </span>
                                {tx.description && (
                                  <span className="text-[10px] text-slate-400 truncate max-w-[200px]" title={tx.description}>
                                    {tx.description}
                                  </span>
                                )}
                              </div>
                            </label>

                            <div className="text-right">
                              <span className="text-[10px] text-slate-400 uppercase font-semibold block">Deuda</span>
                              <span className="font-mono text-rose-600 dark:text-rose-400 font-extrabold text-xs">
                                {formatCurrency(currentDebt)}
                              </span>
                            </div>
                          </div>

                          {/* Individual Account Abono Input (Cuenta a Cuenta) */}
                          {isSelected && (
                            <div className="mt-2.5 pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2 flex-wrap">
                              <div className="flex items-center gap-1.5 flex-1 min-w-[170px]">
                                <span className="text-[11px] font-bold text-slate-500">Abono:</span>
                                <div className="relative flex-1">
                                  <DollarSign className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-2" />
                                  <input
                                    type="number"
                                    step="any"
                                    min="0"
                                    max={currentDebt}
                                    placeholder="0.00"
                                    value={assignedVal}
                                    onChange={e => handleAccountMultiAmountChange(tx.id, e.target.value)}
                                    className="w-full border border-slate-200 dark:border-slate-700 pl-6 pr-2 py-1 rounded-lg bg-slate-50 dark:bg-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-emerald-500 font-mono font-bold text-xs"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handlePaySingleAccountMax(tx)}
                                  className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 px-2 py-1 rounded-lg cursor-pointer transition-colors"
                                  title="Pagar la deuda total de esta cuenta"
                                >
                                  Todo
                                </button>
                              </div>

                              <div className="text-right text-[10.5px]">
                                <span className="text-slate-400">Resta: </span>
                                <span className={`font-mono font-bold ${accountNewRest === 0 ? 'text-emerald-600' : 'text-slate-700 dark:text-slate-300'}`}>
                                  {formatCurrency(accountNewRest)}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Section 3: Summary / Total a pagar ahora */}
                <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-850 border border-slate-200 dark:border-slate-800 space-y-1.5">
                  <div className="flex justify-between items-center text-slate-500 text-xs">
                    <span>Deuda seleccionada total:</span>
                    <span className="font-mono font-bold text-slate-800 dark:text-slate-200">{formatCurrency(totalSelectedDebt)}</span>
                  </div>
                  <div className="flex justify-between items-center text-slate-500 text-xs">
                    <span>Nuevo saldo restante total:</span>
                    <span className={`font-mono font-extrabold ${newRemainingDebt === 0 ? 'text-emerald-600' : 'text-slate-800 dark:text-slate-200'}`}>
                      {formatCurrency(newRemainingDebt)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-slate-200/80 dark:border-slate-800">
                    <span className="font-extrabold text-sm text-slate-900 dark:text-slate-100">Total a pagar ahora:</span>
                    <span className="text-lg font-mono font-extrabold text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(totalToPayNow)}
                    </span>
                  </div>
                </div>

              </div>

              {/* Modal Footer */}
              <div className="p-4 sm:p-5 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-850 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsMultiPaymentOpen(false)}
                  className="btn btn-secondary py-2 px-4 cursor-pointer text-xs"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={totalToPayNow <= 0 || savingPayment || selectedMultiTxIds.length === 0}
                  onClick={handleConfirmMultiPayment}
                  className="btn btn-success py-2 px-4 font-bold flex items-center gap-1.5 cursor-pointer shadow-xs disabled:opacity-50 text-xs"
                >
                  {savingPayment ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Confirmar Abono Múltiple
                </button>
              </div>

            </div>
          </div>
        );
      })()}

      {/* ══════════════════════════════════════════════════
          SUB-MODAL: EDITAR CUENTA
      ══════════════════════════════════════════════════ */}
      {editingTx && (
        <div 
          className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-950/65 backdrop-blur-xs p-4 animate-fade-in"
          onClick={() => setEditingTx(null)}
        >
          <div 
            className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-sm overflow-hidden animate-scale-up"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 sm:p-5 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-850">
              <h3 className="text-sm font-extrabold text-slate-900 dark:text-slate-100">
                Editar Cuenta #{editingTx.id}
              </h3>
              <button 
                type="button"
                onClick={() => setEditingTx(null)} 
                className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const fd = new FormData(e.target as HTMLFormElement);
                const totalAmount = parseFloat(fd.get('total_amount') as string);
                if (isNaN(totalAmount) || totalAmount <= 0) return;

                setSavingTx(true);
                await onUpdateTransaction(editingTx.id, {
                  total_amount: totalAmount,
                  client_name: (fd.get('client_name') as string)?.trim() || client.name,
                });
                setSavingTx(false);
                setEditingTx(null);
              }}
              className="p-5 space-y-4 text-xs"
            >
              <div>
                <label className="block font-bold mb-1 text-slate-700 dark:text-slate-300">Cliente</label>
                <input
                  name="client_name"
                  defaultValue={editingTx.clientName || client.name}
                  className="w-full border dark:border-slate-750 p-2.5 rounded-xl bg-white dark:bg-slate-850 dark:text-slate-200 outline-none focus:ring-2 focus:ring-indigo-500 font-bold"
                  required
                />
              </div>

              <div>
                <label className="block font-bold mb-1 text-slate-700 dark:text-slate-300">Monto Total ($)</label>
                <input
                  name="total_amount"
                  type="number"
                  step="any"
                  defaultValue={editingTx.amount}
                  className="w-full border dark:border-slate-750 p-2.5 rounded-xl bg-white dark:bg-slate-850 dark:text-slate-200 outline-none focus:ring-2 focus:ring-indigo-500 font-mono font-bold"
                  required
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setEditingTx(null)}
                  className="btn btn-secondary py-2 px-4 cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingTx}
                  className="btn btn-primary py-2 px-4 font-bold flex items-center gap-1.5 cursor-pointer shadow-xs"
                >
                  {savingTx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Guardar Cambios
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
